import {
  Award,
  BadgeCheck,
  Flame,
  Footprints,
  GraduationCap,
  ListOrdered,
  MapPin,
  Snowflake,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { cn } from "@/lib/utils";
import {
  BADGE_KEYS,
  doneCount,
  earnedBadges,
  levelOf,
  totalXp,
  type BadgeKey,
  type MissionView,
} from "../domain/training/progress";

const BADGE_ICONS: Record<BadgeKey, LucideIcon> = {
  first_step: Footprints,
  flawless: Sparkles,
  queue_master: ListOrdered,
  fire_fingers: Flame,
  cool_head: Snowflake,
  field_ready: MapPin,
  gavion_seller: BadgeCheck,
  gavion_expert: Award,
  graduate: GraduationCap,
};

/** Level, rank, XP bar, missions done and badges (earned ones lit, the others greyed as goals). */
export const PlayerHeader = ({
  views,
  productViews = [],
  course = "phone",
}: {
  views: readonly MissionView[];
  productViews?: readonly MissionView[];
  course?: "phone" | "product";
}) => {
  const t = useTranslate();
  // one XP total across both courses; the « missions » count is the shown course's
  const xp = totalXp(views) + totalXp(productViews);
  const lvl = levelOf(xp);
  const earned = new Set(earnedBadges(views, productViews));
  const shown = course === "product" ? productViews : views;
  const percent = Math.round((lvl.intoLevel / lvl.levelSize) * 100);
  return (
    <section
      aria-labelledby="gavion-training-rank"
      className="space-y-4 rounded-xl border bg-card p-4 shadow-sm sm:p-6"
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex size-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[0_0_24px_-6px_rgba(247,113,25,.5)]">
          <span className="text-[10px] font-semibold tracking-wide uppercase">
            {t("gavion.training.header.level_short")}
          </span>
          <span className="text-2xl leading-none font-extrabold">
            {lvl.level}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h2 id="gavion-training-rank" className="text-xl font-extrabold">
            {t(`gavion.training.ranks.${lvl.rank}`)}
          </h2>
          <p className="text-sm text-muted-foreground">
            <span className="sr-only">
              {t("gavion.training.header.level", { level: lvl.level })} ·{" "}
            </span>
            <span
              data-testid="training-xp"
              className="font-bold text-foreground"
            >
              {t("gavion.training.header.xp_total", { xp })}
            </span>{" "}
            ·{" "}
            {t("gavion.training.header.missions_done", {
              done: doneCount(shown),
              total: shown.length,
            })}
          </p>
        </div>
      </div>
      <div className="space-y-1">
        <div
          role="progressbar"
          aria-label={t("gavion.training.header.xp_bar", {
            level: lvl.level + 1,
          })}
          aria-valuemin={0}
          aria-valuemax={lvl.levelSize}
          aria-valuenow={lvl.intoLevel}
          className="h-3 w-full overflow-hidden rounded-full bg-primary/15"
        >
          <div
            className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-700"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t("gavion.training.header.xp_next", {
            remaining: lvl.levelSize - lvl.intoLevel,
            level: lvl.level + 1,
          })}
        </p>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-bold">
          {t("gavion.training.header.badges")}
        </h3>
        <ul className="flex flex-wrap gap-2">
          {BADGE_KEYS.map((k) => {
            const Icon = BADGE_ICONS[k];
            const on = earned.has(k);
            return (
              <li
                key={k}
                title={t(`gavion.training.badges.${k}.desc`)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
                  on
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-dashed text-muted-foreground opacity-70",
                )}
              >
                <Icon
                  className={cn("size-3.5", on ? "text-primary" : "")}
                  aria-hidden="true"
                />
                {t(`gavion.training.badges.${k}.name`)}
                <span className="sr-only">
                  {" "}
                  (
                  {on
                    ? t("gavion.training.header.badge_earned")
                    : t("gavion.training.header.badge_locked")}
                  : {t(`gavion.training.badges.${k}.desc`)})
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
